import { TFunction } from 'i18next';
import { UserBlock } from '../types/user-block';
import { UserBlockState } from '../hooks/useUserBlockStatus';

const formatTimestamp = (isoTimestamp: string): string =>
  new Date(isoTimestamp).toLocaleString(undefined, { hour12: false });

/** FusionAuth blocks carry a time window and the name of the user action preventing login. */
const describeBlocks = (blocks: UserBlock[], t: TFunction): string =>
  blocks
    .map((block) => {
      // FusionAuth cannot tell an automatic lockout from a manual ban without the tenant's configured user-action
      // id, so the action's own name is shown rather than a claim about which kind of block this is.
      const reason = block.reason ?? t('userBlock.unknownReason');
      const message = block.blockedUntil
        ? t('userBlock.blockedUntil', { reason, until: formatTimestamp(block.blockedUntil) })
        : t('userBlock.blockedIndefinitely', { reason });

      return block.blockedSince
        ? `${message} ${t('userBlock.blockedSince', { since: formatTimestamp(block.blockedSince) })}`
        : message;
    })
    .join(' ');

/** Tooltip copy for every block state, including the failed lookup that must not read as "not blocked". */
export const describeUserBlockStatus = (
  { status, blocks }: UserBlockState,
  t: TFunction
): string => {
  switch (status) {
    case 'loading':
      return t('userBlock.checking');
    case 'error':
      return t('userBlock.error');
    case 'blocked':
      return describeBlocks(blocks, t);
    case 'notBlocked':
    default:
      return t('userBlock.notBlocked');
  }
};
